import type { ReactNode } from 'react'
import HelpPopover from '../help/HelpPopover'
import type { WorkflowStep } from '../ui/WorkflowStepper'

/**
 * The invention pipeline strip, with the LOCK stated (figure S3).
 *
 * The strip itself is not new — the project page has shown "Workflow Progress"
 * for a long time. What the design is actually about is the thing that was
 * missing: Matching does not open until the review is approved. Without the
 * sentence, a user who reaches Review and sees Matching sitting there greyed
 * out has no way to tell an unfinished feature from a deliberate gate, and the
 * honest answer — that they hold the key and one click turns it — is invisible.
 *
 * The lock is conditioned on the review state and on NOTHING else. A second
 * condition (assessment scores, document count, matching state) would make the
 * caption disappear on a project whose review is genuinely pending, which is
 * both wrong for the user and a silent break of the S3 gate. The approve
 * mutation stays on the page that owns it and arrives here as `action`.
 */
export interface PipelineStepperProps {
  steps: WorkflowStep[]
  /** True once the project's review_status is 'approved'. */
  reviewApproved: boolean
  /** The owner's approve control, rendered by the page that owns the mutation. */
  action?: ReactNode
  /** UX-018: opens the explanation of the lock (the Matches tab's locked card). */
  onExplainLock?: () => void
}

const LOCK_REASON = 'Locked until review is approved'

function caption(step: WorkflowStep, reviewApproved: boolean): { text: string; cls: string } {
  if (step.status === 'completed') return { text: 'Done', cls: 'text-emerald-600' }
  if (step.status === 'active') return { text: "You're here", cls: 'text-iflow-600' }
  if (step.status === 'locked' || (step.label === 'Matching' && !reviewApproved)) {
    return { text: LOCK_REASON, cls: 'text-gray-500' }
  }
  return { text: 'Later', cls: 'text-gray-400' }
}

function dot(step: WorkflowStep, locked: boolean): { cls: string; glyph: string } {
  if (step.status === 'completed') return { cls: 'border-emerald-500 bg-emerald-500 text-white', glyph: '✓' }
  if (step.status === 'active') return { cls: 'border-iflow-500 bg-iflow-500 text-white ring-4 ring-iflow-100', glyph: '●' }
  if (locked) return { cls: 'border-dashed border-slate-300 bg-slate-100 text-gray-500', glyph: '🔒' }
  return { cls: 'border-slate-300 bg-white text-gray-400', glyph: '○' }
}

export default function PipelineStepper({ steps, reviewApproved, action, onExplainLock }: PipelineStepperProps) {
  const active = steps.find((s) => s.status === 'active')
  const stateLabel = active
    ? `${active.label} · ${active.label === 'Review' && !reviewApproved ? 'approve to continue' : 'in progress'}`
    : 'Every stage complete'

  return (
    <div data-part="PipelineStepper" className="light-card mb-4 p-4">
      <div className="mb-2 flex items-center justify-between gap-3">
        <h3 className="flex items-center gap-1.5 text-[13px] font-bold text-iflow-900">
          <span aria-hidden>🛤</span> <span>Workflow Progress</span>
          <HelpPopover
            topic="concept-pipeline"
            title="The pipeline"
            body="Every invention follows the same path: Upload → Intake → Analysis → Assessment → Review → Matching → Disclosure."
            size={14}
          />
        </h3>
        <span className="shrink-0 rounded-full bg-iflow-100 px-2.5 py-0.5 text-[11px] font-semibold text-iflow-700">
          {stateLabel}
        </span>
      </div>

      <ol aria-label="Project pipeline" className="flex flex-wrap items-start gap-y-3">
        {steps.map((step, i) => {
          const locked = step.status === 'locked' || (step.label === 'Matching' && !reviewApproved && step.status !== 'completed')
          const cap = caption(step, reviewApproved)
          const face = dot(step, locked)
          const clickable = !!step.onSelect && !locked
          const Tag = clickable ? 'button' : 'div'
          return (
            <li
              key={step.key}
              className="flex flex-1 items-start"
              aria-current={step.status === 'active' ? 'step' : undefined}
              data-testid={`pipeline-step-${step.key}`}
              data-status={locked ? 'locked' : step.status}
            >
              <Tag
                type={clickable ? 'button' : undefined}
                onClick={clickable ? step.onSelect : undefined}
                title={locked ? 'Matching — Locked until review is approved' : undefined}
                className={`flex w-24 shrink-0 flex-col items-center text-center ${clickable ? 'cursor-pointer' : 'cursor-default'}`}
              >
                <span className={`flex h-7.5 w-7.5 items-center justify-center rounded-full border-2 p-1 text-[12px] font-bold ${face.cls}`}>
                  {face.glyph}
                </span>
                <span className={`mt-1.5 text-[11.5px] leading-tight ${step.status === 'active' ? 'font-bold text-iflow-900' : 'font-semibold text-gray-600'}`}>
                  {step.label}
                </span>
                <span className={`mt-0.5 text-[9.5px] font-bold uppercase tracking-wide ${cap.cls}`}>{cap.text}</span>
                {locked && step.label === 'Matching' && onExplainLock && (
                  <span
                    role="button"
                    tabIndex={0}
                    data-testid="why-locked"
                    onClick={onExplainLock}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onExplainLock() } }}
                    className="mt-1 cursor-pointer text-[10.5px] font-semibold text-iflow-700 underline hover:text-iflow-900"
                  >
                    Why is this locked?
                  </span>
                )}
              </Tag>
              {i < steps.length - 1 && (
                <span
                  aria-hidden
                  className={`mt-3.5 hidden h-0.5 flex-1 sm:block ${step.status === 'completed' ? 'bg-emerald-300' : 'bg-slate-200'}`}
                />
              )}
            </li>
          )
        })}
      </ol>

      {!reviewApproved && (
        // The visible caption under the padlock is the figure's — short, and
        // where the reader is already looking. This is the same fact stated in
        // full for a screen reader (and for the S3 contract row, which pins the
        // sentence rather than the heading, so a no-op could not pass it).
        <p className="sr-only">Matching — Locked until review is approved</p>
      )}
      {action}
    </div>
  )
}
